import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';

import {
  Input,
  BaseButton,
  Modal,
  ToolTip,
  LanguageList,
  useT,
} from '@repo/common';
import ProficiencyLevelList from './ProficiencyLevelList';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/33px/icon_33px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';

const LanguageSkillsForm = ({ language, setResumeData }) => {
  const t = useT();
  const [languageSkills, setLanguageSkills] = useState([
    {
      id: uuidv4(),
      language: '',
      proficiencyLevel: '',
      test: '',
      score: '',
      issueDate: '',
      showTest: false,
    },
  ]);

  const [openLanguageId, setOpenLanguageId] = useState(null);
  const [openProficiencyLevelId, setOpenProficiencyLevelId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [hoverDragAndDropId, setHoverDragAndDropId] = useState(null);
  const [hoverDeleteId, setHoverDeleteId] = useState(null);

  const inputRefs = useRef({});
  const languageRefs = useRef({});
  const proficiencyRefs = useRef({});
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isInitialized) return;
    if (!language || language.length === 0) {
      setLanguageSkills([
        {
          id: uuidv4(),
          language: '',
          iso: '',
          proficiencyLevel: '',
          test: '',
          score: '',
          issueDate: '',
          showTest: false,
        },
      ]);
    } else {
      setLanguageSkills(
        language.map((lang) => ({
          id: uuidv4(),
          language: lang.language || '',
          iso: lang.iso || '',
          proficiencyLevel: lang.proficiencyLevel || '',
          test: lang.test || '',
          score: lang.score || '',
          issueDate: lang.issueDate || '',
          showTest: !!lang.test,
        })),
      );
    }
    setIsInitialized(true);
  }, [language, isInitialized]);

  const addLanguageSkill = () => {
    const newLangSkills = {
      id: uuidv4(),
      language: '',
      proficiencyLevel: '',
      test: '',
      score: '',
      issueDate: '',
    };
    setLanguageSkills((prev) => [...prev, newLangSkills]);
  };

  const openConfirmModal = (id) => {
    setTargetId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    const updated = languageSkills.filter((ls) => ls.id !== targetId);
    setLanguageSkills(updated);
    setResumeData((prev) => ({ ...prev, language: updated }));
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleInputChange = (id, field, value) => {
    const newLanguageSkills = languageSkills.map((ls) =>
      ls.id === id ? { ...ls, [field]: value } : ls,
    );
    setLanguageSkills(newLanguageSkills);

    setResumeData((prev) => ({
      ...prev,
      language: newLanguageSkills,
    }));
  };

  const toggleLanguageList = (id, e) => {
    e.stopPropagation();
    setOpenLanguageId((prev) => (prev === id ? null : id));
  };

  const handleSelectLanguage = (id, lang) => {
    setLanguageSkills((prev) =>
      prev.map((skill) =>
        skill.id === id
          ? { ...skill, language: lang.name, iso: lang.iso }
          : skill,
      ),
    );
    setOpenLanguageId(null);
    inputRefs.current[id]?.blur();
  };

  const toggleProficiencyLevelList = (id, e) => {
    e.stopPropagation();
    setOpenProficiencyLevelId((prev) => (prev === id ? null : id));
  };

  const handleSelectProficiencyLevel = (id, pl) => {
    handleInputChange(id, 'proficiencyLevel', pl);
    setOpenProficiencyLevelId(null);
    inputRefs.current[id]?.blur();
  };

  const handleTestButtonClick = (id) => {
    setLanguageSkills((prev) =>
      prev.map((skill) =>
        skill.id === id ? { ...skill, showTest: true } : skill,
      ),
    );
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(languageSkills);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);

    setLanguageSkills(items);

    setResumeData((prev) => ({
      ...prev,
      language: items,
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openLanguageId) {
        const ref = languageRefs.current[openLanguageId];
        if (ref && !ref.contains(event.target)) {
          setOpenLanguageId(null);
        }
      }

      if (openProficiencyLevelId) {
        const ref = proficiencyRefs.current[openProficiencyLevelId];
        if (ref && !ref.contains(event.target)) {
          setOpenProficiencyLevelId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openLanguageId, openProficiencyLevelId]);

  return (
    <div className='flex flex-col items-start w-full min-w-[460px] max-w-[950px] gap-6'>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId='languageSkills' direction='vertical'>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className='flex flex-col gap-6 w-full'
            >
              {languageSkills.map((langSkill, index) => (
                <Draggable
                  key={langSkill.id}
                  draggableId={langSkill.id}
                  index={index}
                >
                  {(provided, snapshot) => {
                    const style = {
                      ...provided.draggableProps.style,
                      transform: provided.draggableProps.style?.transform
                        ? provided.draggableProps.style.transform.replace(
                            /translate\(([-\d.]+)px,/,
                            'translate(0px,',
                          )
                        : undefined,
                    };

                    if (!snapshot.isDragging) {
                      delete style.transition;
                    }

                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex w-full items-start`}
                      >
                        {/* Drag Handle */}
                        <div
                          {...provided.dragHandleProps}
                          className='relative flex-shrink-0 pt-2 cursor-grab active:cursor-grabbing text-grayscale-gray03 hover:text-icon-success active:text-icon-success'
                          onMouseEnter={() =>
                            setHoverDragAndDropId(langSkill.id)
                          }
                          onMouseLeave={() => setHoverDragAndDropId(null)}
                        >
                          <DragAndDropIcon width={33} height={33} />
                          {hoverDragAndDropId === langSkill.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label='Click and drag to move'
                                labelKr='클릭하고 드래그하여 이동'
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>

                        {/* Language Skills Form */}
                        <div className='flex-grow bg-grayscale-gray00 border border-grayscale-gray02 rounded-[8px] p-6'>
                          <div className='grid w-full grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-6'>
                            {/* Language */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <div
                                ref={(el) =>
                                  (languageRefs.current[langSkill.id] = el)
                                }
                                onClick={(e) =>
                                  toggleLanguageList(langSkill.id, e)
                                }
                                className='relative'
                              >
                                <Input
                                  ref={(el) =>
                                    (inputRefs.current[langSkill.id] = el)
                                  }
                                  label={t('Language', '언어')}
                                  placeholder={t(
                                    'Search and add language',
                                    '언어를 검색해 추가해 주세요',
                                  )}
                                  value={langSkill.language}
                                  readOnly
                                  hideCount
                                  rightIcon={
                                    openLanguageId === langSkill.id ? (
                                      <ArrowTopIcon />
                                    ) : (
                                      <ArrowBottomIcon />
                                    )
                                  }
                                />
                                {openLanguageId === langSkill.id && (
                                  <div className='absolute top-[80px] left-0 z-20 w-full'>
                                    <LanguageList
                                      onSelect={(deg) =>
                                        handleSelectLanguage(langSkill.id, deg)
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Proficiency Level */}
                            <div className='flex flex-col gap-2 min-w-[320px]'>
                              <div
                                ref={(el) =>
                                  (proficiencyRefs.current[langSkill.id] = el)
                                }
                                onClick={(e) =>
                                  toggleProficiencyLevelList(langSkill.id, e)
                                }
                                className='relative'
                              >
                                <Input
                                  ref={(el) =>
                                    (inputRefs.current[langSkill.id] = el)
                                  }
                                  label={t('Proficiency level', '숙련도')}
                                  placeholder={t(
                                    'Select your level',
                                    '숙련도를 선택해 주세요',
                                  )}
                                  value={langSkill.proficiencyLevel}
                                  readOnly
                                  hideCount
                                  rightIcon={
                                    openProficiencyLevelId === langSkill.id ? (
                                      <ArrowTopIcon />
                                    ) : (
                                      <ArrowBottomIcon />
                                    )
                                  }
                                />
                                {openProficiencyLevelId === langSkill.id && (
                                  <div className='absolute top-[80px] left-0 z-20 w-full'>
                                    <ProficiencyLevelList
                                      onSelect={(pl) =>
                                        handleSelectProficiencyLevel(
                                          langSkill.id,
                                          pl,
                                        )
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Add Test or certification */}
                            <div>
                              {!langSkill.showTest && (
                                <BaseButton
                                  type='secondary'
                                  icon={<PlusIcon />}
                                  width='202px'
                                  onClick={() =>
                                    handleTestButtonClick(langSkill.id)
                                  }
                                >
                                  {t(
                                    'Add Test or certification',
                                    '시험 또는 자격증 추가',
                                  )}
                                </BaseButton>
                              )}
                            </div>
                          </div>
                          {langSkill.showTest && (
                            <div className='grid w-full grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-6'>
                              <Input
                                label={t(
                                  'Test or certification (optional)',
                                  '시험 또는 자격증 (선택 사항)',
                                )}
                                placeholder={t(
                                  'e.g. TOPIK II, IELTS, TOEIC, JLPT N2',
                                  '예: TOPIK II, IELTS, TOEIC, JLPT N2',
                                )}
                                value={langSkill.test}
                                onChange={(e) =>
                                  handleInputChange(
                                    langSkill.id,
                                    'test',
                                    e.target.value,
                                  )
                                }
                              />
                              <Input
                                label={t(
                                  'Score or grade (optional)',
                                  '점수 또는 등급 (선택 사항)',
                                )}
                                placeholder={t(
                                  'e.g. Level 6, Band 7.5, 950',
                                  '예: 6급, Band 7.5, 950',
                                )}
                                value={langSkill.score}
                                onChange={(e) =>
                                  handleInputChange(
                                    langSkill.id,
                                    'score',
                                    e.target.value,
                                  )
                                }
                              />
                              <Input
                                label={t(
                                  'Issue date (optional)',
                                  '발급일 (선택 사항)',
                                )}
                                placeholder={t(
                                  'e.g. Aug 2023',
                                  '예: 2023년 8월',
                                )}
                                value={langSkill.issueDate}
                                onChange={(e) =>
                                  handleInputChange(
                                    langSkill.id,
                                    'issueDate',
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                          )}
                        </div>

                        {/* Delete */}
                        <div className='flex-shrink-0 pt-1 relative'>
                          <button
                            className='w-10 h-10 flex justify-center items-center text-grayscale-gray04 hover:text-icon-success transition-colors'
                            onClick={() => openConfirmModal(langSkill.id)}
                            onMouseEnter={() => setHoverDeleteId(langSkill.id)}
                            onMouseLeave={() => setHoverDeleteId(null)}
                          >
                            <DeleteIcon width={24} height={24} />
                          </button>
                          {hoverDeleteId === langSkill.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label='Delete'
                                labelKr='삭제'
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className='w-full pl-[25px] pr-[40px]'>
        <BaseButton
          type='secondary'
          size='l'
          icon={<PlusIcon />}
          width='100%'
          onClick={addLanguageSkill}
          textWrapperClass='flex-none'
        >
          {t('Add one more Language skills', '언어 능력 추가')}
        </BaseButton>
      </div>

      {showDeleteModal && (
        <Modal
          isOpen
          onClickLeftBtn={handleCancelDelete}
          onClickRightBtn={handleConfirmDelete}
          title={t(
            'Delete this language skill entry?',
            '이 언어 능력 항목을 삭제할까요?',
          )}
          subText={t(
            'This action can’t be undone.',
            '이 작업은 취소할 수 없습니다.',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default LanguageSkillsForm;
